import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { FiDownload, FiX, FiSmartphone } from 'react-icons/fi';

const APK_DOWNLOAD_URL = 'https://api.teonila.com/api/v1/app-download';

const InstallPWA = () => {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Detect platform
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const android = /Android/.test(navigator.userAgent);
    setIsIOS(iOS);
    setIsAndroid(android);

    // Check if already installed (PWA mode)
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone === true;

    if (isInstalled) {
      return;
    }

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const daysSinceDismissed = (now - dismissedDate) / (1000 * 60 * 60 * 24);

      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Roles que pueden instalar la app (todos los empleados)
    const installableRoles = ['personal_limpieza', 'supervisor', 'recepcion', 'admin', 'recepcionista', 'encargado', 'admin_establecimiento'];
    const canInstall = user && installableRoles.includes(user.role);

    // For Android: show APK download prompt
    if (android) {
      const timer = setTimeout(() => {
        if (canInstall) {
          setShowInstallPrompt(true);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }

    // For iOS, show custom instructions
    if (iOS) {
      const timer = setTimeout(() => {
        if (canInstall) {
          setShowInstallPrompt(true);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }

    // For Desktop - listen for the beforeinstallprompt event (PWA)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      if (canInstall) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      console.log('PWA installed successfully');
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [user]);

  const handleInstallClick = async () => {
    // Android: direct APK download
    if (isAndroid) {
      window.location.href = APK_DOWNLOAD_URL;
      // Mark as "installed" to not show again
      localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
      setShowInstallPrompt(false);
      return;
    }

    // iOS: show instructions
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    // Desktop PWA
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  // Roles that can install
  const installableRoles = ['personal_limpieza', 'supervisor', 'recepcion', 'admin', 'recepcionista', 'encargado', 'admin_establecimiento'];
  if (!user || !installableRoles.includes(user.role)) {
    return null;
  }

  if (!showInstallPrompt) {
    return null;
  }

  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Instalar en iOS
            </h3>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-500"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-4 text-sm text-gray-600">
            <p>Para instalar esta aplicación en tu dispositivo iOS:</p>

            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>Pulsa el botón de <strong>Compartir</strong> en Safari (el icono con una flecha hacia arriba)</li>
              <li>Desplázate hacia abajo y selecciona <strong>&quot;Añadir a pantalla de inicio&quot;</strong></li>
              <li>Pulsa <strong>&quot;Añadir&quot;</strong> en la esquina superior derecha</li>
            </ol>

            <p className="text-blue-600 font-medium mt-4">
              ¡Listo! La app aparecerá en tu pantalla de inicio.
            </p>
          </div>

          <div className="mt-6">
            <button
              onClick={handleDismiss}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Android APK Download Prompt
  if (isAndroid) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
        <div className="flex items-start">
          <div className="flex-shrink-0 bg-green-100 rounded-full p-2">
            <FiSmartphone className="h-6 w-6 text-green-600" />
          </div>

          <div className="ml-3 flex-1">
            <h3 className="text-sm font-semibold text-gray-900">
              Descargar App Nativa
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Descarga nuestra app Android para una mejor experiencia, notificaciones y acceso rápido.
            </p>

            <div className="mt-4 flex space-x-2">
              <button
                onClick={handleInstallClick}
                className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <FiDownload className="h-4 w-4" />
                Descargar APK
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Ahora no
              </button>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Puede que necesites habilitar &quot;Fuentes desconocidas&quot; en ajustes
            </p>
          </div>

          <button
            onClick={handleDismiss}
            className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Cerrar</span>
            <FiX className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // Default PWA Install Prompt (Desktop/Other)
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
      <div className="flex items-start">
        <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
          <FiDownload className="h-6 w-6 text-blue-600" />
        </div>

        <div className="ml-3 flex-1">
          <h3 className="text-sm font-semibold text-gray-900">
            Instalar Aplicación
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Instala HouseKeeping en tu dispositivo para un acceso más rápido y poder trabajar sin conexión.
          </p>

          <div className="mt-4 flex space-x-2">
            <button
              onClick={handleInstallClick}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Instalar
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Ahora no
            </button>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-500"
        >
          <span className="sr-only">Cerrar</span>
          <FiX className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default InstallPWA;
